/**
 * How To Play Screen
 * The game in plain words for someone who knows Texas Hold'em: the 3×3
 * grid and its 8 lines, the reveal order, Best Hand and Best Color (the
 * split pot), betting, and where the special cards come in. One scrolling
 * page, with small grid pictures. Open to anyone (linked from login too).
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

/** The 9 cells, top-left to bottom-right. */
const CELLS = [0, 1, 2, 3, 4, 5, 6, 7, 8];
const CORNERS = [0, 2, 6, 8];
const DIAMOND = [1, 3, 5, 7];

/** When each cell turns over: 1 corners, 2 diamond, 3 centre. */
const STAGE = (i: number) => (CORNERS.includes(i) ? 1 : DIAMOND.includes(i) ? 2 : 3);
const STAGE_STYLE: Record<number, string> = {
  1: 'bg-sky-700/80 border-sky-400 text-sky-50',
  2: 'bg-violet-700/80 border-violet-400 text-violet-50',
  3: 'bg-amber-600/90 border-amber-300 text-amber-50',
};

/** A small 3×3 grid; `cell` draws each square's look and label. */
const MiniGrid: React.FC<{ cell: (i: number) => { className: string; label?: string }; size?: string }> = ({
  cell,
  size = 'w-24',
}) => (
  <div className={`grid grid-cols-3 gap-1 ${size} shrink-0`} aria-hidden>
    {CELLS.map((i) => {
      const { className, label } = cell(i);
      return (
        <div key={i} className={`aspect-[5/7] rounded border-2 flex items-center justify-center text-sm font-bold ${className}`}>
          {label}
        </div>
      );
    })}
  </div>
);

const LINE_GROUPS = [
  { name: '3 rows', lines: [[0, 1, 2], [3, 4, 5], [6, 7, 8]] },
  { name: '3 columns', lines: [[0, 3, 6], [1, 4, 7], [2, 5, 8]] },
  { name: '2 diagonals', lines: [[0, 4, 8], [2, 4, 6]] },
];
const OFF = 'bg-gray-800 border-gray-700';
const ON = 'bg-emerald-600 border-emerald-300';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-lg font-bold text-amber-300">{title}</h2>
    <div className="space-y-3 text-sm sm:text-base text-gray-300 leading-relaxed">{children}</div>
  </section>
);

const HowToPlayScreen: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <header className="sticky top-0 z-10 bg-gray-900/80 border-b border-gray-800 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-4">
          <button
            // Back to wherever you came from (login, the lobby or a game).
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/lobby'))}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Back
          </button>
          <div className="text-lg font-bold text-white">How to Play</div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-8">
        <p className="text-gray-300">
          PokerCubed is built on Texas Hold'em: two hole cards, shared cards in the middle, best five-card hand. But
          the shared cards sit in a grid, every card also has a jewel, and each pot is won twice over. Here's what's
          different.
        </p>

        <Section title="1. Your hand: 2 hole cards + 1 line of the grid">
          <p>
            You get <b className="text-white">2 hole cards</b> of your own. Instead of five shared cards in a row, there
            are <b className="text-white">nine in a 3×3 grid</b>.
          </p>
          <p>
            Your hand is always your <b className="text-white">2 hole cards plus the 3 cards of one line</b> of the
            grid. There are 8 lines:
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-4">
            {LINE_GROUPS.map(({ name, lines }) => (
              <div key={name} className="space-y-1.5">
                <div className="flex gap-2">
                  {lines.map((line) => (
                    <MiniGrid key={line.join()} size="w-14" cell={(i) => ({ className: line.includes(i) ? ON : OFF })} />
                  ))}
                </div>
                <div className="text-xs text-gray-400">{name}</div>
              </div>
            ))}
          </div>
          <p>
            You don't choose the line: <b className="text-white">the game finds your best one for you</b>. You can't
            mix cards from different lines, and you always use both hole cards.
          </p>
        </Section>

        <Section title="2. The grid turns over in three steps">
          <div className="flex gap-4 items-center">
            <MiniGrid cell={(i) => ({ className: STAGE_STYLE[STAGE(i)], label: String(STAGE(i)) })} />
            <ol className="space-y-1.5">
              <li><b className="text-sky-300">1. The 4 corners</b></li>
              <li><b className="text-violet-300">2. The diamond</b>: the 4 edge cards</li>
              <li><b className="text-amber-300">3. The centre</b>, last of all</li>
            </ol>
          </div>
          <p>
            After the diamond, the 4 outside lines (top, bottom, left and right) are complete. The centre card is in the
            other 4 lines (the middle row, the middle column and both diagonals), so it's the{' '}
            <b className="text-white">most powerful card on the board</b>, and it can change everything at the end.
          </p>
        </Section>

        <Section title="3. Jewels, and a pot won twice">
          <p>
            Every card also carries one of <b className="text-white">4 jewels</b>:{' '}
            <span className="text-red-400 font-semibold">Ruby</span>,{' '}
            <span className="text-emerald-400 font-semibold">Emerald</span>,{' '}
            <span className="text-yellow-300 font-semibold">Topaz</span> and{' '}
            <span className="text-gray-200 font-semibold">Onyx</span>. There are 13 of each, spread across the deck at
            random every hand, so a jewel tells you nothing about the card's rank or suit.
          </p>
          <p>
            Every pot is <b className="text-white">split in half</b>, and the same cards are judged two ways:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-gray-700 bg-gray-800/60 p-3">
              <div className="font-bold text-white">🏆 Best Hand: half the pot</div>
              <p className="text-sm mt-1">
                Normal poker hands, from High Card up to Royal Flush, just as in Hold'em.
              </p>
            </div>
            <div className="rounded-xl border border-gray-700 bg-gray-800/60 p-3">
              <div className="font-bold text-white">💎 Best Color: the other half</div>
              <p className="text-sm mt-1">
                The jewels count like ranks: Pair, Two Pair, Three of a Kind, Full House, Four and Five of a Kind (all
                five the same jewel, the best). With only 4 jewels, every hand has at least a pair.
              </p>
            </div>
          </div>
          <p>
            Your best line for Best Hand and your best line for Best Color can be different lines. One player can win
            both halves, or two players can win one each. The table shows your Best Hand and Best Color as the cards
            turn.
          </p>
        </Section>

        <Section title="4. Betting">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              <b className="text-white">Antes, no blinds.</b> Everyone pays the ante before each hand. The ante usually
              goes up as the game goes on.
            </li>
            <li>
              <b className="text-white">Four betting rounds:</b> after the deal, after the corners, after the diamond
              and after the centre.
            </li>
            <li>
              <b className="text-white">No-limit.</b> The smallest bet is the ante; a raise must be at least the last
              bet or raise in that round. You can always go all-in.
            </li>
            <li>
              <b className="text-white">The "1st" chip</b> sits by one seat for a whole game. That player opens every
              betting round, and the rest follow in seat order. The chip moves on one seat each game, so acting last
              is shared out fairly.
            </li>
            <li>
              Everyone starts a game with the same chips. Run out and you can watch the rest or leave; the last player
              with chips wins the game.
            </li>
          </ul>
        </Section>

        <Section title="5. Special cards">
          <p>
            Some hands are dealt a <b className="text-white">special card</b> beside the hole cards. At first it's face
            down: a gold <b className="text-amber-300">Special ?</b>. It turns over once the corners are revealed, and
            then you find out whether it <b className="text-emerald-400">Helps</b> or{' '}
            <b className="text-rose-400">Hinders</b>. That's why a weak hand can be worth staying in for.
          </p>
          <p>
            Specials can change a card, hide part of it from you, show you your opponents' cards, change which lines
            you can use, or limit how you can bet. No two players hold the same special in a hand. Fold before the
            corners and you never find out what yours was.
          </p>
          <p>
            Press and hold a special in a game to see what it does, or see them all on the{' '}
            <Link to="/specials" className="text-amber-300 underline hover:text-amber-200">
              Special Cards
            </Link>{' '}
            page.
          </p>
        </Section>
      </div>
    </div>
  );
};

export default HowToPlayScreen;
